/**
 * Satellite Insights: the farm's crop progress, as the farmer app's Crop guide
 * works it out (GuideHero). The crop calendar and the day count are copied
 * verbatim from farmer-app (src/config/cropCalendar.js and
 * src/services/cropGuideModel.js); `cropProgress` returns exactly the fields
 * of cropGuideModel.buildGuide that the guide's top card shows.
 *
 * Pure functions, no React.
 */

/* -------- crop calendar (farmer-app src/config/cropCalendar.js) -------- */

/** Land preparation starts this many days before sowing. */
export const PREP_DAYS = 15;

const stage = (id, from, to, keywords = []) => ({ id, nameKey: `cropStage_${id}`, from, to, keywords });

export const CROP_CALENDAR = {
  wheat: {
    total: 130,
    varieties: ['HD-3086', 'HD-2967', 'PBW-343'],
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('wheatSprouting', 0, 7),
      stage('wheatCrownRoots', 8, 25, ['cri', 'crown root']),
      stage('wheatSpreading', 26, 45, ['tillering']),
      stage('wheatStem', 46, 65, ['stem elongation', 'jointing', 'booting']),
      stage('wheatFlowering', 66, 85, ['flowering', 'heading', 'ear']),
      stage('wheatGrainFilling', 86, 110, ['grain filling', 'milking', 'dough']),
      stage('harvest', 111, 130, ['maturity', 'harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [20, 25], 4: [30, 45], 5: [60, 85], 6: [86, 110], 7: [120, 130] },
  },
  mustard: {
    total: 130,
    varieties: ['Pusa Bold', 'RH-749', 'Varuna'],
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('mustardSprouting', 0, 10),
      stage('mustardThinning', 11, 30),
      stage('mustardBranching', 31, 50, ['branching', 'bud', 'pre-flowering']),
      stage('mustardFlowering', 51, 75, ['flowering']),
      stage('mustardPods', 76, 110, ['pod', 'siliqua', 'seed filling']),
      stage('harvest', 111, 130, ['maturity', 'harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [15, 30], 4: [30, 50], 5: [50, 90], 6: [120, 130] },
  },
  rice: {
    total: 120,
    varieties: [],
    stages: [
      stage('riceNursery', -PREP_DAYS, -1),
      stage('riceSettling', 0, 10),
      stage('riceTillering', 11, 40, ['tillering']),
      stage('ricePanicle', 41, 60, ['panicle', 'booting']),
      stage('riceFlowering', 61, 80, ['flowering', 'heading']),
      stage('riceGrainFilling', 81, 105, ['grain filling', 'milking', 'dough']),
      stage('harvest', 106, 120, ['maturity', 'harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [20, 40], 4: [45, 75], 5: [110, 120] },
  },
  maize: {
    total: 110,
    varieties: [],
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('maizeSprouting', 0, 10),
      stage('maizeEarly', 11, 25, ['four leaf', 'four leave', 'early vegetative']),
      stage('maizeKneeHigh', 26, 40, ['knee', 'eight leaf', 'eight leave']),
      stage('maizePreTassel', 41, 55, ['pre-tassel']),
      stage('maizeTasselSilk', 56, 70, ['tassel', 'silk', 'flowering']),
      stage('maizeGrainFilling', 71, 95, ['grain filling']),
      stage('harvest', 96, 110, ['maturity', 'harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [15, 25], 4: [30, 35], 5: [45, 55], 6: [60, 90], 7: [90, 110], 8: [105, 110] },
  },
  potato: {
    total: 100,
    varieties: [],
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('potatoSprouting', 0, 15, ['sprouting']),
      stage('potatoVegetative', 16, 30, ['vegetative']),
      stage('potatoTuberInit', 31, 45, ['tuber initiation']),
      stage('potatoBulking', 46, 70, ['bulking']),
      stage('potatoMaturing', 71, 90, ['maturation', 'maturity']),
      stage('harvest', 91, 100, ['harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [15, 30], 4: [30, 45], 5: [45, 70], 6: [70, 90], 7: [95, 100] },
  },
  sugarcane: {
    total: 330,
    varieties: [],
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('caneSprouting', 0, 30, ['sprouting', 'germination']),
      stage('caneEarly', 31, 60),
      stage('caneTillering', 61, 120, ['tillering']),
      stage('caneGrandGrowth', 121, 210, ['grand growth', 'elongation']),
      stage('caneMaturity', 211, 300, ['maturity', 'ripening']),
      stage('harvest', 301, 330, ['harvest']),
    ],
    tipWindows: { 1: [-PREP_DAYS, -1], 2: [0, 0], 3: [30, 60], 4: [60, 120], 5: [120, 210], 6: [210, 300], 7: [320, 330] },
  },
};

/** How long an unknown crop is assumed to stand when its report gives no later day. */
export const DEFAULT_TOTAL_DAYS = 120;

/**
 * A crop the guide has no calendar for still gets a timeline: preparation,
 * sowing, growing and harvest, sized to the latest day its recommendation
 * names. Its tasks are the report's own; there are no general practices.
 */
export const genericCalendar = (total = DEFAULT_TOTAL_DAYS) => {
  const end = Math.max(30, Math.round(total));
  return {
    total: end,
    varieties: [],
    generic: true,
    stages: [
      stage('before', -PREP_DAYS, -1),
      stage('sowing', 0, 7),
      stage('growing', 8, end - 16),
      stage('harvest', end - 15, end, ['maturity', 'harvest']),
    ],
    tipWindows: {},
  };
};

/**
 * The calendar key for a crop as the report, a booking or the farmer names it.
 * Same folding the report PDF uses (backend-app pdf-helper/makeReport.js
 * normalizeCropName), so the guide and the PDF agree on which crop tips apply.
 */
export const cropKey = (name) => {
  if (!name || typeof name !== 'string') return null;
  const lower = name.toLowerCase().trim().replace(/^organic\s+/, '');
  if (!lower) return null;
  if (lower.includes('potato')) return 'potato';
  if (lower.includes('wheat')) return 'wheat';
  if (lower.includes('maize') || lower.includes('corn')) return 'maize';
  if (lower.replace(/\s+/g, '').includes('sugarcane')) return 'sugarcane';
  if (lower.includes('mustard')) return 'mustard';
  if (lower.includes('rice') || lower.includes('paddy')) return 'rice';
  return lower;
};

/** The calendar for a crop, or null when the guide has none for it. */
export const calendarFor = (name) => CROP_CALENDAR[cropKey(name)] || null;

/* -------- days (farmer-app src/services/cropGuideModel.js) -------- */

const DAY_MS = 24 * 60 * 60 * 1000;

const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** A YYYY-MM-DD as that calendar day at local midnight. */
const localDay = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** Days from sowing to today: 0 on the sowing day, negative before it. */
export const dayOfCrop = (sowingDate, today = new Date()) => {
  const sown = localDay(sowingDate);
  if (!sown) return null;
  return Math.round((midnight(today).getTime() - sown.getTime()) / DAY_MS);
};

/* -------- the guide's top card (farmer-app cropGuideModel.buildGuide) -------- */

/**
 * Where the farm's crop is today, from its crop plan.
 *
 * `{ status: 'noPlan' }` without a crop and a sowing date, as buildGuide.
 * Otherwise the crop and variety, whether it is a general plan, the day of the
 * crop, its stage, the sowing date and how far it is from preparation to the
 * harvest day. A crop the calendar does not know gets the generic calendar of
 * the default length (the farmer app can stretch it further from the soil
 * report's tasks, which this page does not read).
 *
 * @param {{crop, variety, sowing_date, sowing_status}|null} plan
 * @param {Date} [today]
 */
export const cropProgress = (plan, today = new Date()) => {
  if (!plan || !plan.crop || !plan.sowing_date) return { status: 'noPlan' };

  const crop = cropKey(plan.crop);
  const day = dayOfCrop(plan.sowing_date, today);
  if (day == null) return { status: 'noPlan' };

  let calendar = calendarFor(crop);
  // buildGuide: genericCalendar(lastDay + 15), lastDay = max(DEFAULT_TOTAL_DAYS - 15, the soil tasks' last day).
  if (!calendar) calendar = genericCalendar(DEFAULT_TOTAL_DAYS - 15 + 15);

  const current = calendar.stages.find((s) => day >= s.from && day <= s.to) || null;
  const first = calendar.stages[0];

  return {
    status: 'ready',
    crop,
    cropName: plan.crop,
    variety: plan.variety || null,
    sown: plan.sowing_status === 'sown',
    sowingDate: plan.sowing_date,
    generic: Boolean(calendar.generic),
    day,
    total: calendar.total,
    stage: current
      ? { id: current.id, nameKey: current.nameKey }
      : { id: null, nameKey: day < first.from ? 'cropStage_notStarted' : 'cropStage_finished' },
    progressPct: Math.max(2, Math.min(100, ((day + PREP_DAYS) / (calendar.total + PREP_DAYS)) * 100)),
  };
};
